"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { X, ArrowRight } from "lucide-react";

interface RedirectNotificationProps {
  targetUrl: string;
  targetName: string;
  initialCountdown?: number;
  onCancel?: () => void;
}

export function RedirectNotification({ 
  targetUrl, 
  targetName, 
  initialCountdown = 10,
  onCancel 
}: RedirectNotificationProps) {
  const [countdown, setCountdown] = useState(initialCountdown);
  const [isVisible, setIsVisible] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const router = useRouter();

  useEffect(() => {
    if (!isVisible || countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setIsRedirecting(true);
          // Small delay to show the redirecting state
          setTimeout(() => {
            router.push(targetUrl);
          }, 500);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown, isVisible, targetUrl, router]);

  const handleCancel = () => {
    setIsVisible(false);
    onCancel?.();
  };

  const handleRedirectNow = () => {
    setIsRedirecting(true);
    router.push(targetUrl);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-2">
      <Card className="border-primary/20 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/90 shadow-lg max-w-md">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <ArrowRight className="h-4 w-4 text-primary" />
                <p className="font-medium text-sm">
                  {isRedirecting ? "Redirecting..." : "Welcome back!"}
                </p>
              </div>
              
              {!isRedirecting ? (
                <>
                  <p className="text-sm text-muted-foreground mb-3">
                    Redirecting you to {targetName} in{" "}
                    <span className="font-mono font-semibold text-primary">
                      {countdown}
                    </span>{" "}
                    seconds
                  </p>
                  
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      onClick={handleRedirectNow}
                      className="flex-1"
                    >
                      Go Now
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={handleCancel}
                      className="px-3"
                    >
                      Stay
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="animate-spin h-3 w-3 border-2 border-primary border-t-transparent rounded-full"></div>
                  <p className="text-sm text-muted-foreground">
                    Taking you to {targetName}...
                  </p>
                </div>
              )}
            </div>
            
            {!isRedirecting && (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCancel}
                className="h-6 w-6 p-0 hover:bg-muted"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
          
          {/* Progress bar */}
          {!isRedirecting && (
            <div className="mt-3 w-full bg-muted rounded-full h-1">
              <div 
                className="bg-primary h-1 rounded-full transition-all duration-1000 ease-linear"
                style={{ 
                  width: `${((initialCountdown - countdown) / initialCountdown) * 100}%` 
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
